import React, { useContext, useState } from "react";

import { Button, Grid, TextField } from "@mui/material";

import { allocateModuleComponents } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { MODULE_ACTION_BUTTON_MIN_WIDTH } from "constants/index";
import { GrpcError, SnackbarContext } from "context/snackbar-context";

interface AllocateModuleComponentsProps {
  orgId: string;
  regionCode: string;
  moduleName: string;
  subscriptionId: string;
  currentQuantity: number;
  onSuccess: () => void;
}

const AllocateModuleComponents = ({
  orgId,
  regionCode,
  moduleName,
  subscriptionId,
  currentQuantity,
  onSuccess,
}: AllocateModuleComponentsProps) => {
  const { showError } = useContext(SnackbarContext);
  const [openDialog, setOpenDialog] = useState(false);
  const [quantityInput, setQuantityInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const handleOpenDialog = () => {
    setQuantityInput(String(currentQuantity));
    setOpenDialog(true);
  };

  const parsedQuantity = Number(quantityInput);
  const isApplyDisabled =
    isSaving ||
    quantityInput.trim() === "" ||
    Number.isNaN(parsedQuantity) ||
    parsedQuantity < 0;

  const handleConfirm = async () => {
    if (isApplyDisabled) return;
    setIsSaving(true);
    try {
      await allocateModuleComponents(
        { orgId, subscriptionId, quantity: parsedQuantity },
        callOptionsHeaders(regionCode, orgId),
      );
      setOpenDialog(false);
      onSuccess();
    } catch (err) {
      showError(err as GrpcError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Button
        sx={{ mt: 1, mr: 2, minWidth: MODULE_ACTION_BUTTON_MIN_WIDTH }}
        data-testid={`${moduleName}-allocate-components`}
        variant="contained"
        color="primary"
        onClick={handleOpenDialog}
      >
        UPDATE QUANTITY
      </Button>

      <ConfirmDialog
        title="Update Quantity"
        cancelBtnLabel="CANCEL"
        confirmBtnLabel="APPLY"
        open={openDialog}
        setOpen={setOpenDialog}
        disabled={isApplyDisabled}
        disableCancelBtn={isSaving}
        disableCloseOnConfirm
        onConfirm={handleConfirm}
        onCancel={() => setOpenDialog(false)}
      >
        <Grid
          container
          direction="column"
          spacing={2}
          sx={{ mt: 0 }}
          data-testid={`${moduleName}-allocate-components-form`}
        >
          <Grid item>
            <TextField
              fullWidth
              size="small"
              type="number"
              variant="outlined"
              label="Quantity"
              value={quantityInput}
              onChange={(e) => setQuantityInput(e.target.value)}
              InputProps={{
                inputProps: {
                  min: 0,
                  "data-testid": `${moduleName}-allocate-components-quantity`,
                },
              }}
            />
          </Grid>
        </Grid>
      </ConfirmDialog>
    </>
  );
};

export default AllocateModuleComponents;
